/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2023 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2023 - Finnish Meteorological Institute (FMI)
 * Copyright 2024 - The Norwegian Meteorological Institute (MET Norway)
 * */

import type { SxProps } from '@mui/material';
import {
  Box,
  Collapse,
  ListItem,
  ListItemButton,
  List as MuiList,
  ListItemText,
  Grid,
} from '@mui/material';
import type { FC } from 'react';
import React from 'react';
import type { RowComponentProps } from 'react-window';
import { List, useListRef } from 'react-window';
import { CustomToggleButton, SearchHighlight } from '@opengeoweb/shared';
import { ChevronDown, ChevronUp } from '@opengeoweb/theme';
import type { TimeseriesCollectionDetail } from './utils';
import {
  filterCollectionsParametersFromService,
  getCollectionKey,
} from './utils';
import type {
  CollectionSelectionTarget,
  ServiceFilterChipsObjectEntities,
} from '../../store/types';
import { useTimeseriesTranslation } from '../../utils/i18n';
import type { Parameter, Plot } from '../TimeSeries/types';
import { ParameterInfoButtonConnect } from '../TimeSeriesManager/ParameterInfo/ParameterInfoButtonConnect';

const RowComponent = ({
  index,
  style,
  filteredCollectionList,
  collectionListItemDetails,
  setCollectionListItemDetails,
  handleAddOrRemoveClick,
  searchFilter,
  panelId,
  selectPlot,
  t,
}: RowComponentProps<{
  filteredCollectionList: TimeseriesCollectionDetail[];
  collectionListItemDetails: Record<string, boolean>;
  setCollectionListItemDetails: (newList: Record<string, boolean>) => void;
  handleAddOrRemoveClick: (
    parameter: Parameter,
    plotHasParameter: boolean,
  ) => void;
  searchFilter: string;
  panelId: string;
  selectPlot: Plot;
  t: ReturnType<typeof useTimeseriesTranslation>['t'];
}>): React.ReactElement => {
  const collectionListItem = filteredCollectionList[index];
  const collectionKey = getCollectionKey(collectionListItem);

  return (
    <Box style={style}>
      <ListItem
        sx={{
          backgroundColor: 'geowebColors.cards.cardContainer',
          height: '44px',
          marginTop: 0.5,
          borderWidth: '1px',
          borderStyle: 'solid',
          borderRadius: '3px',
          padding: 0,
          zIndex: 10,
          overflow: 'hidden',
          borderColor: 'geowebColors.cards.cardContainerBorder',
          '.MuiListItemText-primary': {
            fontSize: '12px',
            fontWeight: 500,
            textOverflow: 'ellipsis',
            overflow: 'hidden',
          },
        }}
      >
        <ListItemButton
          onClick={(): void => {
            const currentState = collectionListItemDetails[collectionKey];
            setCollectionListItemDetails({
              ...collectionListItemDetails,
              [collectionKey]: !currentState,
            });
          }}
          sx={{
            height: '100%',
            paddingLeft: '24px',
            paddingRight: '8px',
          }}
        >
          <Grid
            container
            size="grow"
            sx={{
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Grid size="grow">
              <Box sx={fontStyling()}>
                <SearchHighlight
                  search={searchFilter}
                  text={
                    collectionListItem.collectionTitle ||
                    collectionListItem.collectionId
                  }
                />
              </Box>

              <Box sx={fontStyling(false)}>
                <SearchHighlight
                  search={searchFilter}
                  text={collectionListItem.serviceName}
                />
              </Box>
            </Grid>
            <Grid>
              <ListItemText
                primary={`${collectionListItem.parameters.length} ${t('timeseries-results')}`}
                sx={{
                  paddingLeft: 1,
                  paddingRight: 5,
                  textAlign: 'right',
                }}
              />
            </Grid>
          </Grid>
          {collectionListItemDetails[collectionKey] ? (
            <ChevronUp />
          ) : (
            <ChevronDown />
          )}
        </ListItemButton>
      </ListItem>
      <Collapse in={collectionListItemDetails[collectionKey]} unmountOnExit>
        <MuiList
          sx={{
            paddingBottom: `${DEFAULT_PARAMTER_LIST_PADDING_BOTTOM}px`,
            paddingTop: 0,
            backgroundColor: 'geowebColors.cards.cardContainer',
            borderWidth: `${DEFAULT_PARAMTER_LIST_BORDER_BOTTOM}px`,
            borderStyle: 'none solid solid solid',
            borderColor: 'geowebColors.cards.cardContainerBorder',
            borderBottomLeftRadius: '3px',
            borderBottomRightRadius: '3px',
            boxShadow: 1,
            top: -5,
          }}
        >
          {collectionListItem?.parameters.length < 1 && (
            <ListItem
              role="listitem"
              sx={{
                height: `${DEFAULT_PARAMETER_ROW_HEIGHT + 4}px`,
                minHeight: '36px',
                paddingLeft: '40px',
                paddingRight: '4px',
                paddingTop: '13px',
                ' .MuiListItemText-primary': {
                  fontSize: '12px',
                  fontWeight: 500,
                  whiteSpace: 'nowrap',
                  textOverflow: 'ellipsis',
                  overflow: 'hidden',
                },
              }}
            >
              <ListItemText
                secondary={t('timeseries-no-params')}
                sx={{
                  paddingLeft: 1,
                  paddingRight: 1,
                }}
              />
            </ListItem>
          )}
          {collectionListItem.parameters.map((parameter) => {
            const plotParameter = selectPlot.parameters!.find(
              (plotParameter: {
                serviceId: string;
                collectionId: string;
                propertyName: string;
              }) =>
                collectionListItem.serviceId === plotParameter.serviceId &&
                collectionListItem.collectionId ===
                  plotParameter.collectionId &&
                parameter.propertyName === plotParameter.propertyName,
            );
            const plotHasParameter = Boolean(plotParameter);
            const paramDisplayName = parameter.propertyLabel
              ? `${parameter.propertyLabel}   (${parameter.propertyName})`
              : parameter.propertyName;
            return (
              <ListItem
                aria-label={paramDisplayName}
                key={parameter.propertyName}
                role="listitem"
                sx={{
                  paddingLeft: '40px',
                  paddingRight: '4px',
                  ':not(:first-of-type)': {
                    borderTopColor: 'geowebColors.cards.cardContainerBorder',
                    borderTopStyle: 'solid',
                    borderTopWidth: '1px',

                    height: `${DEFAULT_PARAMETER_ROW_HEIGHT}px`,
                    minHeight: '32px',
                  },
                  ':first-of-type': {
                    height: `${DEFAULT_PARAMETER_ROW_HEIGHT + 4}px`,
                    minHeight: '36px',
                    paddingTop: '13px',
                  },
                  ':hover': {
                    backgroundColor:
                      'geowebColors.cards.cardContainerMouseOver',
                  },
                }}
              >
                <Grid
                  container
                  size="grow"
                  sx={{
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <Grid size="grow">
                    <Box sx={fontStyling()}>
                      <SearchHighlight
                        search={searchFilter}
                        text={paramDisplayName}
                      />
                    </Box>
                  </Grid>
                  <Grid>
                    <Box
                      sx={{
                        marginRight: '8px',
                      }}
                    >
                      <ParameterInfoButtonConnect
                        panelId={panelId}
                        parameter={
                          plotParameter ?? {
                            ...parameter,
                            plotId: selectPlot.plotId,
                          }
                        }
                      />
                      <CustomToggleButton
                        fullWidth={true}
                        onClick={(): void => {
                          const parameterWithPlotId: Parameter = {
                            ...parameter,
                            plotId: selectPlot.plotId,
                            id: plotParameter?.id,
                          };
                          handleAddOrRemoveClick(
                            parameterWithPlotId,
                            plotHasParameter,
                          );
                        }}
                        selected={plotHasParameter}
                        sx={{
                          width: '60px',
                          height: '24px !important',
                          marginLeft: '4px',
                        }}
                        variant="tool"
                      >
                        {plotHasParameter ? 'Remove' : 'Add'}
                      </CustomToggleButton>
                    </Box>
                  </Grid>
                </Grid>
              </ListItem>
            );
          })}
        </MuiList>
      </Collapse>
    </Box>
  );
};

export const NO_RESULTS_FOUND_MESSAGE = 'No parameters found';
export const DEFAULT_DIALOG_HEIGHT = 500;
const DEFAULT_PADDING_TOP = 6;
const DEFAULT_RESULTS_HEIGHT = 22;

const DEFAULT_COLLECTION_ROW_HEIGHT = 48; // listItem height + 4px marginTop
const DEFAULT_PARAMETER_ROW_HEIGHT = 34; // listItem height
const DEFAULT_PARAMTER_LIST_PADDING_BOTTOM = 0;
const DEFAULT_PARAMTER_LIST_BORDER_BOTTOM = 1;

const fontStyling = (primary = true): SxProps => {
  return {
    fontSize: '12px',
    height: '14px',
    lineHeight: '14px',
    fontWeight: primary ? 500 : 'normal',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
  };
};

interface TimeSeriesSelectListProps {
  selectPlot: Plot;
  panelId: string;
  collectionListItemDetails: Record<string, boolean>;
  collectionList?: TimeseriesCollectionDetail[];
  setCollectionListItemDetails: (newList: Record<string, boolean>) => void;
  handleAddOrRemoveClick: (
    parameter: Parameter,
    plotHasParameter: boolean,
  ) => void;
  searchFilter: string;
  filterChipsServices: ServiceFilterChipsObjectEntities;
  allFilterChipsEnabled: boolean;
  collectionSelection?: CollectionSelectionTarget;
}

export const TimeSeriesSelectList: FC<TimeSeriesSelectListProps> = ({
  selectPlot,
  panelId,
  collectionListItemDetails,
  collectionList,
  handleAddOrRemoveClick,
  setCollectionListItemDetails,
  searchFilter,
  filterChipsServices,
  allFilterChipsEnabled,
  collectionSelection,
}) => {
  const listRef = useListRef(null);
  const lastScrolledTarget = React.useRef<
    CollectionSelectionTarget | undefined
  >(undefined);
  const [filteredCollectionList, setFilteredCollectionList] = React.useState<
    TimeseriesCollectionDetail[]
  >([]);
  const { t } = useTimeseriesTranslation();

  React.useEffect(() => {
    if (!collectionList) {
      return;
    }
    const filterResults = filterCollectionsParametersFromService(
      collectionList,
      searchFilter,
      filterChipsServices,
      allFilterChipsEnabled,
    );
    setFilteredCollectionList(filterResults);
  }, [
    searchFilter,
    collectionList,
    filterChipsServices,
    allFilterChipsEnabled,
  ]);

  React.useEffect(() => {
    if (
      !collectionSelection ||
      lastScrolledTarget.current === collectionSelection
    ) {
      return;
    }
    const index = filteredCollectionList.findIndex(
      (collection) =>
        getCollectionKey(collection) === getCollectionKey(collectionSelection),
    );
    if (index >= 0 && listRef.current) {
      listRef.current.scrollToRow({ index, align: 'start' });
      lastScrolledTarget.current = collectionSelection;
    }
  }, [collectionSelection, filteredCollectionList, listRef]);

  return (
    <Box
      data-testid="timeseriesSelectList"
      sx={{
        flex: 1,
        minHeight: 0,
        overflow: 'hidden',
      }}
    >
      <Box sx={{ marginBottom: '4px', fontSize: '12px' }}>
        {filteredCollectionList.length} {t('timeseries-results')}
      </Box>
      <List
        listRef={listRef}
        rowComponent={RowComponent}
        rowCount={filteredCollectionList.length}
        rowHeight={(index: number): number => {
          const collectionKey = getCollectionKey(filteredCollectionList[index]);

          if (!collectionListItemDetails[collectionKey]) {
            return DEFAULT_COLLECTION_ROW_HEIGHT;
          }
          if (filteredCollectionList[index].parameters.length < 1) {
            return (
              DEFAULT_PARAMETER_ROW_HEIGHT +
              DEFAULT_COLLECTION_ROW_HEIGHT +
              DEFAULT_PARAMTER_LIST_PADDING_BOTTOM +
              DEFAULT_PARAMTER_LIST_BORDER_BOTTOM
            );
          }
          return (
            filteredCollectionList[index].parameters.length *
              DEFAULT_PARAMETER_ROW_HEIGHT +
            DEFAULT_COLLECTION_ROW_HEIGHT +
            DEFAULT_PARAMTER_LIST_PADDING_BOTTOM +
            DEFAULT_PARAMTER_LIST_BORDER_BOTTOM
          );
        }}
        rowProps={{
          collectionListItemDetails,
          setCollectionListItemDetails,
          filteredCollectionList,
          handleAddOrRemoveClick,
          searchFilter,
          panelId,
          selectPlot,
          t,
        }}
        style={{
          width: '100%',
          height: `calc(100% - ${DEFAULT_RESULTS_HEIGHT + DEFAULT_PADDING_TOP}px)`,
        }}
      />
    </Box>
  );
};
